/**
 * Open-Meteo API Service
 *
 * Gratis väder-API som täcker hela världen.
 * Ingen API-nyckel krävs!
 * Dokumentation: https://open-meteo.com/en/docs
 */

import type { LatLng, Result, WeatherForecast, WeatherSymbol } from '@/types';

const OPEN_METEO_BASE = 'https://api.open-meteo.com/v1/forecast';

/**
 * Mappa Open-Meteo WMO-koder till våra WeatherSymbol
 * https://open-meteo.com/en/docs#weathervariables
 */
function wmoCodeToWeatherSymbol(code: number, isDay: boolean): WeatherSymbol {
  // WMO Weather interpretation codes (WW)
  // 0: Clear sky
  // 1, 2, 3: Mainly clear, partly cloudy, overcast
  // 45, 48: Fog
  // 51, 53, 55: Drizzle
  // 56, 57: Freezing drizzle
  // 61, 63, 65: Rain
  // 66, 67: Freezing rain
  // 71, 73, 75: Snow
  // 77: Snow grains
  // 80, 81, 82: Rain showers
  // 85, 86: Snow showers
  // 95: Thunderstorm
  // 96, 99: Thunderstorm with hail

  if (code === 0) return isDay ? 1 : 1; // Clear sky
  if (code <= 2) return isDay ? 2 : 2; // Partly cloudy
  if (code === 3) return 6; // Overcast
  if (code === 45 || code === 48) return 6; // Fog -> cloudy
  if (code >= 51 && code <= 57) return 7; // Drizzle -> light rain
  if (code >= 61 && code <= 67) return 8; // Rain
  if (code >= 71 && code <= 77) return 15; // Snow
  if (code >= 80 && code <= 82) return 10; // Rain showers
  if (code >= 85 && code <= 86) return 16; // Snow showers
  if (code >= 95) return 11; // Thunderstorm

  return 6; // Default: cloudy
}

/**
 * Hämta väderprognos från Open-Meteo
 */
export async function fetchOpenMeteoForecast(
  location: LatLng
): Promise<Result<WeatherForecast[]>> {
  try {
    const url = new URL(OPEN_METEO_BASE);
    url.searchParams.set('latitude', location.lat.toFixed(6));
    url.searchParams.set('longitude', location.lon.toFixed(6));
    url.searchParams.set('hourly', 'temperature_2m,relative_humidity_2m,precipitation,weather_code,wind_speed_10m,wind_direction_10m,cloud_cover,is_day');
    url.searchParams.set('timezone', 'auto');
    url.searchParams.set('forecast_days', '10');

    const response = await fetch(url.toString());

    if (!response.ok) {
      return {
        success: false,
        error: new Error(`Open-Meteo API error: ${response.status} ${response.statusText}`),
      };
    }

    const data = await response.json();
    const forecasts = parseOpenMeteoForecasts(data, location);

    return { success: true, data: forecasts };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Parsa Open-Meteo response till WeatherForecast[]
 */
function parseOpenMeteoForecasts(data: OpenMeteoResponse, location: LatLng): WeatherForecast[] {
  const forecasts: WeatherForecast[] = [];
  const hourly = data.hourly;

  if (!hourly || !hourly.time) {
    return forecasts;
  }

  for (let i = 0; i < hourly.time.length; i++) {
    const time = hourly.time[i];
    const temp = hourly.temperature_2m?.[i];
    const humidity = hourly.relative_humidity_2m?.[i];
    const precipitation = hourly.precipitation?.[i];
    const weatherCode = hourly.weather_code?.[i];
    const windSpeed = hourly.wind_speed_10m?.[i];
    const windDirection = hourly.wind_direction_10m?.[i];
    const cloudCover = hourly.cloud_cover?.[i];
    const isDay = hourly.is_day?.[i];

    if (time === undefined || temp === undefined) continue;

    forecasts.push({
      validTime: new Date(time),
      temperature: temp,
      humidity: humidity ?? 0,
      precipitation: precipitation ?? 0,
      weatherSymbol: wmoCodeToWeatherSymbol(weatherCode ?? 0, isDay === 1),
      windSpeed: windSpeed ?? 0,
      windDirection: windDirection ?? 0,
      cloudCover: cloudCover ?? 0,
      location,
    });
  }

  return forecasts;
}

interface OpenMeteoResponse {
  hourly?: {
    time?: string[];
    temperature_2m?: number[];
    relative_humidity_2m?: number[];
    precipitation?: number[];
    weather_code?: number[];
    wind_speed_10m?: number[];
    wind_direction_10m?: number[];
    cloud_cover?: number[];
    is_day?: number[];
  };
}
